import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { EntityType } from '~/enums/EntityType';
import { SearchQuery } from '~/features/advanced-search/types';
import { useFormatNumber } from '~/i18n/hooks/useFormatNumber/useFormatNumber';

import { useGetDocumentCount } from '../hooks';

interface Props {
  entityId: string | null;
  entityType: EntityType.DocumentAttachedData | EntityType.Field;
  query: SearchQuery;
}

export const DocumentsInScope = ({ query, entityId, entityType }: Props) => {
  const intl = useIntl();
  const formatNumber = useFormatNumber();
  const { toast } = useToast();

  const { docsInScope, isLoadingDocsInScope, errorDocsInScope } =
    useGetDocumentCount({ entityId, entityType, query });

  const [defaultTotal, setDefaultTotal] = useState(0);

  useEffect(() => {
    setDefaultTotal(docsInScope);
  }, [query, docsInScope, entityId]);

  if (errorDocsInScope) {
    toast({
      status: 'danger',
      message: intl.formatMessage({
        id: 'DocumentsInScope.failedToGetDocuments',
        defaultMessage: 'Failed to get documents in scope',
        description:
          'Error message when failing to retrieve documents in scope',
      }),
    });
  }

  return (
    <ContentContainer
      loadingContent={{
        isLoading: isLoadingDocsInScope,
        shimmer: { height: '2rem', width: '100%' },
      }}
    >
      <Layout preset="xs">
        <Text>
          {intl.formatMessage({
            id: 'DocumentsInScope.documentsInScopeLabel',
            defaultMessage: 'Documents in Scope: ',
            description: 'Label for the number of documents in scope',
          })}
        </Text>
        <Text color="status.active">{formatNumber(defaultTotal)}</Text>
      </Layout>
    </ContentContainer>
  );
};
